// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { Modal as PingModal } from '../../components/Modal';
import { createTheme } from '../../design-system/theme';
import { leaderboardStrings } from './strings';
import type { LeaderboardColumn, LeaderboardColumnId } from './types';

export interface AddLeaderboardEntryDraft {
  readonly values: ReadonlyMap<LeaderboardColumnId, string | number | null>;
}

export interface AddLeaderboardEntryModalProps {
  readonly visible: boolean;
  readonly columns: readonly LeaderboardColumn[];
  readonly onClose: () => void;
  readonly onSubmit: (draft: AddLeaderboardEntryDraft) => void;
}

function parseValue(raw: string, column: LeaderboardColumn): string | number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return null;
  }
  if (column.kind === 'number') {
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return trimmed;
}

export const AddLeaderboardEntryModal: React.FC<AddLeaderboardEntryModalProps> = ({
  visible,
  columns,
  onClose,
  onSubmit,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');
  const [drafts, setDrafts] = useState<Map<LeaderboardColumnId, string>>(() => new Map());

  useEffect(() => {
    if (!visible) {
      setDrafts(new Map());
    }
  }, [visible]);

  const updateDraft = useCallback((id: LeaderboardColumnId, value: string) => {
    setDrafts((current) => {
      const next = new Map(current);
      next.set(id, value);
      return next;
    });
  }, []);

  const handleSubmit = useCallback(() => {
    const values = new Map<LeaderboardColumnId, string | number | null>();
    for (const column of columns) {
      const raw = drafts.get(column.id) ?? '';
      values.set(column.id, parseValue(raw, column));
    }
    onSubmit({ values });
    onClose();
  }, [columns, drafts, onSubmit, onClose]);

  return (
    <PingModal
      visible={visible}
      onClose={onClose}
      accessibilityLabel={leaderboardStrings.addEntryTitle}
    >
      <View>
        <Text style={[styles.title, { color: theme.colors.text.primary }]}>
          {leaderboardStrings.addEntryTitle}
        </Text>
        <ScrollView style={styles.fields}>
          {columns.map((column) => (
            <View key={column.id} style={styles.field}>
              <Text style={[styles.label, { color: theme.colors.text.secondary }]}>
                {column.title}
              </Text>
              <TextInput
                value={drafts.get(column.id) ?? ''}
                onChangeText={(value) => updateDraft(column.id, value)}
                placeholder={leaderboardStrings.columnKind(column.kind)}
                placeholderTextColor={theme.colors.text.tertiary}
                accessibilityLabel={column.title}
                keyboardType={column.kind === 'number' ? 'numeric' : 'default'}
                style={[
                  styles.input,
                  {
                    color: theme.colors.text.primary,
                    borderColor: theme.colors.border.primary,
                    backgroundColor: theme.colors.surface.secondary,
                  },
                ]}
              />
            </View>
          ))}
        </ScrollView>
        <View style={styles.actions}>
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel={leaderboardStrings.cancelCta}
            style={({ pressed }) => [styles.actionButton, pressed && { opacity: 0.7 }]}
          >
            <Text style={[styles.cancelText, { color: theme.colors.text.secondary }]}>
              {leaderboardStrings.cancelCta}
            </Text>
          </Pressable>
          <Pressable
            onPress={handleSubmit}
            accessibilityRole="button"
            accessibilityLabel={leaderboardStrings.saveCta}
            style={({ pressed }) => [
              styles.actionButton,
              styles.primaryButton,
              { backgroundColor: theme.colors.accent.indigo },
              pressed && { opacity: 0.85 },
            ]}
          >
            <Text style={styles.primaryText}>{leaderboardStrings.saveCta}</Text>
          </Pressable>
        </View>
      </View>
    </PingModal>
  );
};

const styles = StyleSheet.create({
  title: {
    fontSize: 17,
    fontWeight: '700',
    marginBottom: 12,
  } satisfies TextStyle,
  fields: {
    maxHeight: 360,
  } satisfies ViewStyle,
  field: {
    marginBottom: 12,
  } satisfies ViewStyle,
  label: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 6,
    textTransform: 'uppercase',
  } satisfies TextStyle,
  input: {
    fontSize: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  } satisfies TextStyle,
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 12,
  } satisfies ViewStyle,
  actionButton: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    marginLeft: 8,
  } satisfies ViewStyle,
  primaryButton: {
    minWidth: 80,
    alignItems: 'center',
  } satisfies ViewStyle,
  cancelText: {
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
  primaryText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
  } satisfies TextStyle,
});
